import { Link } from "react-router-dom";
import { CardData } from "../HomeCourseCard/HomeCourseCard";
import "./ProgramCard.scss";

interface ProgramCardProps {
  course: CardData;
  // External destination (e.g. Learnova); otherwise the course's internal page.
  href?: string;
  cta?: string;
  // "detailed" adds status, badges, prerequisites and instructor, for the catalog.
  variant?: "compact" | "detailed";
}

const ProgramCard = ({ course, href, cta, variant = "compact" }: ProgramCardProps) => {
  const detailed = variant === "detailed";
  const label = cta ?? course.buttonText ?? "Program Details";
  // "/" marks a program without its own page yet.
  const hasPage = !!course.actionProgramDetails && course.actionProgramDetails !== "/";

  const isOpen = !!course.currentState?.toLowerCase().includes("open");
  const badges = detailed
    ? [
        course.isNewCourse && "New Course",
        course.isNewWorkshop && "New Workshop",
        course.isOneDayWorkShop && "One Day Workshop",
      ].filter(Boolean)
    : [];
  const intake = detailed && course.isNewIntake ? course.newIntakeText : undefined;

  const body = (
    <>
      <div className="ss-pcard__media">
        <img
          src={course.cardImage}
          alt={course.cardImageAlt ?? course.title}
          loading="lazy"
        />
        {course.type && (
          <span
            className={`ss-pcard__type ${
              course.type === "Workshop" ? "ss-pcard__type--workshop" : ""
            }`}
          >
            {course.type}
          </span>
        )}
        {detailed && course.isOverlay && course.overlayText && (
          <span className="ss-pcard__flag">{course.overlayText}</span>
        )}
      </div>
      <div className="ss-pcard__plate">
        <h3>{course.title?.trim()}</h3>
      </div>
      <div className="ss-pcard__body">
        <ul className="ss-pcard__chips">
          {course.duration && (
            <li>
              <i className="bi bi-clock" aria-hidden="true"></i>
              {course.duration}
            </li>
          )}
          {course.difficulty && (
            <li>
              <i className="bi bi-bar-chart" aria-hidden="true"></i>
              {course.difficulty}
            </li>
          )}
          {!!course.starCount && (
            <li>
              <i className="bi bi-star-fill" aria-hidden="true"></i>
              {course.starCount}
              {!!course.ratingCount && (
                <span className="ss-pcard__muted">({course.ratingCount})</span>
              )}
            </li>
          )}
        </ul>

        {detailed && (badges.length > 0 || intake || course.IsSpecialization) && (
          <div className="ss-pcard__tags">
            {course.IsSpecialization && course.specialization && (
              <span className="ss-pcard__tag ss-pcard__tag--spec">
                <i className="bi bi-bookmark-star" aria-hidden="true"></i>
                {course.specialization}
              </span>
            )}
            {intake && (
              <span
                className={`ss-pcard__tag ${isOpen ? "ss-pcard__tag--intake" : ""}`}
              >
                {intake}
              </span>
            )}
            {badges.map((badge) => (
              <span className="ss-pcard__tag" key={String(badge)}>
                {badge}
              </span>
            ))}
          </div>
        )}

        {course.description && (
          <p className="ss-pcard__desc">{course.description}</p>
        )}

        {detailed && (
          <dl className="ss-pcard__meta">
            {course.enrollmentDeadline && (
              <div>
                <dt>Enrollment</dt>
                <dd>
                  Registration closed. Registered participants enroll by{" "}
                  {course.enrollmentDeadline}.
                </dd>
              </div>
            )}
            {course.prereq && (
              <div>
                <dt>Prerequisites</dt>
                <dd className="ss-pcard__clamp">{course.prereq}</dd>
              </div>
            )}
            {course.conductedBy && (
              <div>
                <dt>Conducted by</dt>
                <dd>{course.conductedBy}</dd>
              </div>
            )}
          </dl>
        )}

        <div className="ss-pcard__footer">
          {detailed && course.currentState && (
            <span
              className={`ss-pcard__status ${
                isOpen
                  ? "ss-pcard__status--open"
                  : ""
              }`}
            >
              {course.currentState}
            </span>
          )}
          {detailed && !course.starCount && (
            <span className="ss-pcard__muted ss-pcard__unrated">Not rated yet</span>
          )}
          {(href || hasPage) && (
            <span className="ss-pcard__cta">
              {label}
              <i className="bi bi-arrow-right" aria-hidden="true"></i>
            </span>
          )}
        </div>
      </div>
    </>
  );

  if (href) {
    return (
      <a className="ss-pcard" href={href} target="_blank" rel="noopener noreferrer">
        {body}
      </a>
    );
  }
  if (!hasPage) {
    return <div className="ss-pcard ss-pcard--static">{body}</div>;
  }
  return (
    <Link className="ss-pcard" to={course.actionProgramDetails}>
      {body}
    </Link>
  );
};

export default ProgramCard;
